import { Component, ElementRef, Input, OnInit, } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { atLeastOne, claimNumberValidator, noBlankFieldValidator, policyNumberValidator, dateOfLossValidator, futureDateValidator } from '../../models/validators';
import { ClaimService } from '../../services/claim.service';
import { PolicyService } from '../../services/policy.service';
import { SearchInformation } from '../../classes/search-information';

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './search.component.html',
  styleUrl: './search.component.css'
})
export class SearchComponent implements OnInit {
  methods = ["Claim Number", "Policy Number"];
  @Input() claimNumber = '';
  @Input() policyNumber = '';
  @Input() dol = '';
  @Input() firstName = '';
  @Input() lastName = '';
  @Input() selectedMethod = this.methods[0]

  isInvalidClaimNumber;
  isInvalidPolicyNumber = false;
  isInvalidDOL = false;
  isInvalidFirstName = false;
  isInvalidLastName = false;

  claimErrorMsg: string;
  policyErrorMsg: string;
  dateOfLossErrorMsg: string;
  firstNameErrorMsg: string;
  lastNameErrorMsg: string;

  searchForm = new FormGroup({
    selectedMethod: new FormControl(this.selectedMethod, { validators: Validators.required }),
    searchMethod: new FormGroup({
      claimNumber: new FormControl(this.claimNumber, { validators: [Validators.minLength(9), claimNumberValidator(), noBlankFieldValidator()] }),
      policyNumber: new FormControl(this.policyNumber, { validators: [Validators.minLength(12),  noBlankFieldValidator()] }),
    }, { validators: [atLeastOne(Validators.required, ['claimNumber', 'policyNumber'])] }),
    dateOfLoss: new FormControl(this.dol, { validators: [Validators.required, Validators.minLength(10), dateOfLossValidator(), futureDateValidator()] }),
    firstName: new FormControl(this.firstName, { validators: Validators.required }),
    lastName: new FormControl(this.lastName, { validators: Validators.required })
  })

  constructor(private claimService: ClaimService, private policyService: PolicyService, private searchInfo: SearchInformation, private element: ElementRef) { }

  ngOnInit(): void {
    document.getElementById("selectLabel").classList.add("-oneX--floatUp");
  }

  ngDoCheck(): void {
    document.getElementById("selectLabel").classList.add("-oneX--floatUp");
    window['oneX']?.addElement(this.element.nativeElement);
  }

  ngAfterViewInit() {
    window['oneX']?.addElement(this.element.nativeElement);
  }

  dropDownValue() {
    const searchByClaim = (this.searchForm.controls.selectedMethod.value == this.methods[0]) ? true : false
    return searchByClaim

  }

  fieldErrors() {
    var selMethVal: string = this.searchForm.controls.selectedMethod.value
    switch (selMethVal) {
      case 'Claim Number': {
        this.errorInClaimNumber()
        break;
      }
      case 'Policy Number': {
        this.errorInPolicyNumber()
        break;
      }
    }
  }

  errorInClaimNumber() {
    if (this.searchForm.controls.searchMethod.errors?.['atLeastOne']) {
      this.isInvalidClaimNumber = true;
      this.claimErrorMsg = 'Empty field. Enter a valid State Farm Claim Number.'
      document.getElementById('claimInput').setAttribute('aria-invalid', 'true')
    } else if (this.searchForm.controls.searchMethod.controls.claimNumber.errors?.['minlength']) {
      this.isInvalidClaimNumber = true;
      this.claimErrorMsg = 'A valid claim number is 9 digits in length.'
      document.getElementById('claimInput').setAttribute('aria-invalid', 'true')
    } else if (this.searchForm.controls.searchMethod.controls.claimNumber.errors?.['incorrectFormat']) {
      this.isInvalidClaimNumber = true;
      this.claimErrorMsg = 'Incorrect format.  Enter a valid Claim Number in 99-XXXX-XXX format.'
      document.getElementById('claimInput').setAttribute('aria-invalid', 'true')
    } else {
      this.isInvalidClaimNumber = false;
      document.getElementById('claimInput').setAttribute('aria-invalid', 'false')
    }
  }

  errorInPolicyNumber() {
    if (this.searchForm.controls.searchMethod.errors?.['atLeastOne']) {
      this.isInvalidPolicyNumber = true;
      this.policyErrorMsg = 'Empty field. Enter a valid State Farm Policy Number.'
      document.getElementById('policyInput').setAttribute('aria-invalid', 'true')
    } else if (this.searchForm.controls.searchMethod.controls.policyNumber.errors?.['minlength']) {
      this.isInvalidPolicyNumber = true;
      this.policyErrorMsg = 'Minimum length is 12 digits. Please enter a valid Policy Number in  XXXXXXX-A99-99A format.'
      document.getElementById('policyInput').setAttribute('aria-invalid', 'true')
    } else if (this.searchForm.controls.searchMethod.controls.policyNumber.errors?.['incorrectFormat']) {
      this.isInvalidPolicyNumber = true;
      this.policyErrorMsg = 'Incorrect format. Please enter a valid Policy Number in XXXXXXX-A99-99A format.'
      document.getElementById('policyInput').setAttribute('aria-invalid', 'true')
    } else {
      this.isInvalidPolicyNumber = false;
      document.getElementById('policyInput').setAttribute('aria-invalid', 'false')
    }
  }

  errorInDateOfLoss() {
    if (this.searchForm.controls.dateOfLoss.errors?.['required']) {
      this.isInvalidDOL = true;
      this.dateOfLossErrorMsg = "Empty field. Enter a valid Date of Loss in MM-DD-YYYY format.";
      document.getElementById('dateInput').setAttribute('aria-invalid', 'true')
    } else if (this.searchForm.controls.dateOfLoss.errors?.['minlength']) {
      this.isInvalidDOL = true;
      this.dateOfLossErrorMsg = "Incorrect format. Enter a valid Date of Loss in MM-DD-YYYY format."
      document.getElementById('dateInput').setAttribute('aria-invalid', 'true')
    } else if (this.searchForm.controls.dateOfLoss.errors?.['incorrectDate']) {
      this.isInvalidDOL = true;
      this.dateOfLossErrorMsg = "Incorrect format. Enter a valid Date of Loss in MM-DD-YYYY format."
      document.getElementById('dateInput').setAttribute('aria-invalid', 'true')
    } else if (this.searchForm.controls.dateOfLoss.errors?.['futureDate']) {
      this.isInvalidDOL = true;
      this.dateOfLossErrorMsg = "Entered date is in the future."
      document.getElementById('dateInput').setAttribute('aria-invalid', 'true')
    } else {
      this.isInvalidDOL = false;
      document.getElementById('dateInput').setAttribute('aria-invalid', 'false')
    }
  }

  errorInFirstName() {
    if (this.searchForm.controls.firstName.errors?.['required']) {
      this.isInvalidFirstName = true;
      this.firstNameErrorMsg = 'Empty field. Enter a First Name'
      document.getElementById('fnameInput').setAttribute('aria-invalid', 'true')
    } else {
      this.isInvalidFirstName = false;
      document.getElementById('fnameInput').setAttribute('aria-invalid', 'false')
    }
  }

  errorInLastName() {
    if (this.searchForm.controls.lastName.errors?.['required']) {
      this.isInvalidLastName = true;
      this.lastNameErrorMsg = 'Empty field. Enter a Last Name'
      document.getElementById('lnameInput').setAttribute('aria-invalid', 'true')
    } else {
      this.isInvalidLastName = false;
      document.getElementById('lnameInput').setAttribute('aria-invalid', 'false')
    }
  }

  onSubmit() {
    if (this.searchForm.invalid) {
      this.fieldErrors()
      this.errorInDateOfLoss()
      this.errorInFirstName()
      this.errorInLastName()
    }
    if (!this.searchForm.invalid) {
      let claimNumber = this.searchForm.controls.searchMethod.controls.claimNumber.value;
      let policyNumber = this.searchForm.controls.searchMethod.controls.policyNumber.value;
      let dol = this.searchForm.controls.dateOfLoss.value;
      let firstName = this.searchForm.controls.firstName.value;
      let lastName = this.searchForm.controls.lastName.value;
      let search = this.dropDownValue() ? this.claimService.buildRequest(claimNumber, dol, firstName, lastName) : this.policyService.buildRequest(policyNumber, dol, firstName, lastName)
      this.searchForm.controls.searchMethod.controls.claimNumber.reset();
      this.searchForm.controls.searchMethod.controls.policyNumber.reset();
      return search

    }
  }

  selectedMethodFunc(e: any) {
    this.searchForm.controls.selectedMethod?.setValue(e.target.value, { onlySelf: true });
    this.searchForm.controls.searchMethod.controls.claimNumber.reset();
    this.searchForm.controls.searchMethod.controls.policyNumber.reset();
  }

  ngOnDestroy() {
    window['oneX']?.removeElement(this.element.nativeElement);
  }
}


